import type { StatsigUser } from '@statsig/react-bindings';
import { isEqual, omit } from 'lodash';
import { isProduction } from '~/apps/Router/router.utils';

/**
 * Set to true to enable console logging in debugStatsigUserDifferences
 * When true, will log detailed differences to console when users are different
 * Useful for debugging unnecessary Statsig user updates
 */
const isToDebugToConsole = !isProduction();

/**
 * Properties to ignore when comparing StatsigUser objects
 * These are metadata properties that don't affect Statsig's core functionality
 * and should not trigger unnecessary user updates
 *
 * - statsigEnvironment: Environment metadata (development/production) that doesn't
 *   affect feature flag evaluation or user segmentation
 */
const IGNORED_PROPERTIES = ['statsigEnvironment'] as const;

/**
 * Deep comparison function for StatsigUser objects
 * Returns true if the users are different (need update), false if they're the same
 * Note: Reference equality doesn't matter - only value equality matters
 * Ignores metadata properties like statsigEnvironment
 * Optionally logs debug information if isToDebugToConsole is enabled
 */
export const areStatsigUsersDifferent = (
    user1: StatsigUser | null | undefined,
    user2: StatsigUser | null | undefined
): boolean => {
    const normalizedUser1 = normalizeUserForComparison(user1);
    const normalizedUser2 = normalizeUserForComparison(user2);
    const areDifferent = !isEqual(normalizedUser1, normalizedUser2);

    // Debug logging if enabled and users are different
    if (isToDebugToConsole && areDifferent) {
        debugStatsigUserDifferences(user1, user2);
    }

    return areDifferent;
};

/**
 * Creates a normalized version of a StatsigUser for comparison purposes
 * Removes properties that should be ignored in comparisons and normalizes object prototypes
 */
const normalizeUserForComparison = (user: StatsigUser | null | undefined) => {
    if (!user) {
        return user;
    }

    // Remove ignored properties
    const withoutIgnored = omit(user, IGNORED_PROPERTIES);

    // Normalize objects to plain objects to avoid prototype differences
    return {
        ...withoutIgnored,
        custom: withoutIgnored.custom
            ? JSON.parse(JSON.stringify(withoutIgnored.custom))
            : withoutIgnored.custom,
        customIDs: withoutIgnored.customIDs
            ? JSON.parse(JSON.stringify(withoutIgnored.customIDs))
            : withoutIgnored.customIDs
    };
};

/**
 * Pure detection utility that finds specific differences between two StatsigUser objects
 * Returns detailed information about what's different without any side effects
 */
const detectStatsigUserDifferences = (
    user1: StatsigUser | null | undefined,
    user2: StatsigUser | null | undefined
): {
    areDifferent: boolean;
    differences: string[];
    user1Type: string;
    user2Type: string;
} => {
    const differences: string[] = [];
    const user1Type =
        user1 === null ? 'null' : user1 === undefined ? 'undefined' : 'object';
    const user2Type =
        user2 === null ? 'null' : user2 === undefined ? 'undefined' : 'object';

    // Check null/undefined cases
    if (!user1 && !user2) {
        return { areDifferent: false, differences: [], user1Type, user2Type };
    }
    if (!user1 || !user2) {
        differences.push(`One user is ${user1Type}, the other is ${user2Type}`);
        return { areDifferent: true, differences, user1Type, user2Type };
    }

    // Check each property
    if (user1.userID !== user2.userID) {
        differences.push(`userID: "${user1.userID}" vs "${user2.userID}"`);
    }

    if (user1.email !== user2.email) {
        differences.push(`email: "${user1.email}" vs "${user2.email}"`);
    }

    // Deep check custom object
    if (!isEqual(user1.custom, user2.custom)) {
        // Find specific custom property differences
        const custom1Keys = Object.keys(user1.custom || {});
        const custom2Keys = Object.keys(user2.custom || {});
        const allKeys = [...new Set([...custom1Keys, ...custom2Keys])];

        const customDifferences: string[] = [];
        for (const key of allKeys) {
            const val1 = user1.custom?.[key];
            const val2 = user2.custom?.[key];
            if (!isEqual(val1, val2)) {
                customDifferences.push(
                    `custom.${key}: ${JSON.stringify(val1)} vs ${JSON.stringify(
                        val2
                    )}`
                );
            }
        }

        if (customDifferences.length > 0) {
            differences.push(...customDifferences);
        } else {
            // Deep investigation when we can't identify specific differences
            const custom1 = user1.custom;
            const custom2 = user2.custom;

            // Check types
            const type1 = custom1 === null ? 'null' : typeof custom1;
            const type2 = custom2 === null ? 'null' : typeof custom2;

            if (type1 !== type2) {
                differences.push(
                    `custom objects have different types: ${type1} vs ${type2}`
                );
            } else {
                // Check constructors
                const constructor1 = custom1?.constructor?.name || 'undefined';
                const constructor2 = custom2?.constructor?.name || 'undefined';

                if (constructor1 !== constructor2) {
                    differences.push(
                        `custom objects have different constructors: ${constructor1} vs ${constructor2}`
                    );
                } else {
                    // Check JSON serialization
                    const json1 = JSON.stringify(custom1);
                    const json2 = JSON.stringify(custom2);

                    if (json1 !== json2) {
                        differences.push(
                            `custom objects have different JSON serialization lengths: ${json1.length} vs ${json2.length} chars`
                        );

                        // Show first difference in JSON
                        for (
                            let i = 0;
                            i < Math.min(json1.length, json2.length);
                            i++
                        ) {
                            if (json1[i] !== json2[i]) {
                                const context = Math.max(0, i - 20);
                                const snippet1 = json1.substring(
                                    context,
                                    i + 20
                                );
                                const snippet2 = json2.substring(
                                    context,
                                    i + 20
                                );
                                differences.push(
                                    `  First JSON difference at position ${i}:`
                                );
                                differences.push(
                                    `    Object1: ...${snippet1}...`
                                );
                                differences.push(
                                    `    Object2: ...${snippet2}...`
                                );
                                break;
                            }
                        }
                    } else {
                        // Deep prototype and descriptor analysis
                        const proto1 = Object.getPrototypeOf(custom1);
                        const proto2 = Object.getPrototypeOf(custom2);

                        if (proto1 !== proto2) {
                            differences.push(
                                `custom objects have different prototypes:`
                            );
                            differences.push(
                                `  Object1 prototype: ${
                                    proto1?.constructor?.name || 'null'
                                }`
                            );
                            differences.push(
                                `  Object2 prototype: ${
                                    proto2?.constructor?.name || 'null'
                                }`
                            );
                        }

                        // Check property descriptors
                        const keys1 = Object.keys(custom1 || {});
                        const keys2 = Object.keys(custom2 || {});
                        const allKeys = [...new Set([...keys1, ...keys2])];

                        const descriptorDiffs: string[] = [];
                        for (const key of allKeys) {
                            const desc1 = Object.getOwnPropertyDescriptor(
                                custom1,
                                key
                            );
                            const desc2 = Object.getOwnPropertyDescriptor(
                                custom2,
                                key
                            );

                            if (!isEqual(desc1, desc2)) {
                                descriptorDiffs.push(
                                    `  ${key}: descriptor1=${JSON.stringify(
                                        desc1
                                    )} vs descriptor2=${JSON.stringify(desc2)}`
                                );
                            }
                        }

                        if (descriptorDiffs.length > 0) {
                            differences.push(
                                `custom objects have different property descriptors:`
                            );
                            differences.push(...descriptorDiffs);
                        }

                        if (proto1 === proto2 && descriptorDiffs.length === 0) {
                            differences.push(
                                `custom objects are different but have identical JSON, properties, prototypes, and descriptors (unknown difference)`
                            );
                        }
                    }
                }
            }
        }
    }

    // Deep check customIDs object
    if (!isEqual(user1.customIDs, user2.customIDs)) {
        differences.push(`customIDs objects are different`);

        // Find specific customIDs property differences
        const ids1Keys = Object.keys(user1.customIDs || {});
        const ids2Keys = Object.keys(user2.customIDs || {});
        const allKeys = [...new Set([...ids1Keys, ...ids2Keys])];

        for (const key of allKeys) {
            const val1 = user1.customIDs?.[key];
            const val2 = user2.customIDs?.[key];
            if (!isEqual(val1, val2)) {
                differences.push(
                    `  customIDs.${key}: ${JSON.stringify(
                        val1
                    )} vs ${JSON.stringify(val2)}`
                );
            }
        }
    }

    // Check for any other properties (excluding statsigEnvironment which is just metadata)
    const user1Keys = Object.keys(user1);
    const user2Keys = Object.keys(user2);
    const allUserKeys = [...new Set([...user1Keys, ...user2Keys])];

    for (const key of allUserKeys) {
        // Skip properties we already checked or want to ignore
        if (
            [
                'userID',
                'email',
                'custom',
                'customIDs',
                'statsigEnvironment'
            ].includes(key)
        ) {
            continue;
        }

        const val1 = (user1 as any)[key];
        const val2 = (user2 as any)[key];
        if (!isEqual(val1, val2)) {
            differences.push(
                `${key}: ${JSON.stringify(val1)} vs ${JSON.stringify(val2)}`
            );
        }
    }

    return {
        areDifferent: differences.length > 0,
        differences,
        user1Type,
        user2Type
    };
};

/**
 * Debug utility that detects and logs differences between two StatsigUser objects
 * Only logs to console when differences are found
 */
const debugStatsigUserDifferences = (
    user1: StatsigUser | null | undefined,
    user2: StatsigUser | null | undefined
): void => {
    const result = detectStatsigUserDifferences(user1, user2);

    if (result.areDifferent) {
        console.log(
            'Updating StatsigUser because of differences:',
            result.differences
        );
        // console.log('From:', user1);
        // console.log('To:', user2);
    }
};
